/**
 * SIMPLE Reservation Update Handler
 *
 * Bypasses all the complexity and just formats form data for the backend API
 * Works directly with the data structure that AddEditBooking.jsx already creates
 */

export class SimpleReservationUpdateHandler {
  constructor(orderApiResponse, paymentApiResponse) {
    // Extract order data
    this.orderData =
      orderApiResponse?.currentData?.data?.[0] ||
      orderApiResponse?.data?.data?.[0] ||
      {};
    this.paymentData =
      paymentApiResponse?.currentData?.data?.[0] ||
      paymentApiResponse?.data?.data?.[0] ||
      {};

    // Get the real order ID
    this.orderId =
      this.orderData.orderID ||
      this.orderData.orderId ||
      orderApiResponse?.originalArgs?.orderID ||
      0;

    console.log("=== SIMPLE HANDLER INITIALIZED ===");
    console.log("Order ID:", this.orderId);
    console.log("Order data exists:", !!this.orderData);
    console.log("Payment data exists:", !!this.paymentData);
  }

  /**
   * Process the update - SIMPLE approach
   */
  processUpdate(formData) {
    try {
      console.log("=== SIMPLE UPDATE PROCESS ===");
      console.log("Form data received:", formData);

      // Build the update payload directly from form data
      const payload = this.buildUpdatePayload(formData);

      return {
        success: true,
        payload,
        changes: { message: "Direct form data processing" },
      };
    } catch (error) {
      console.error("Simple update processing error:", error);
      return {
        success: false,
        error: error.message,
        payload: null,
      };
    }
  }

  /**
   * Build the update payload - DIRECT from form data
   */
  buildUpdatePayload(formData) {
    console.log("=== BUILDING SIMPLE PAYLOAD ===");

    const payload = {
      // Basic order info
      orderID: this.orderId,
      phoneNumber: formData.phoneNumber || "",
      orderDate:
        formData.orderDate || formData.checkInDate || new Date().toISOString(),
      checkOutDate: formData.checkOutDate || "",

      // Financial info - ensure numbers are valid
      subTotal: Number(formData.subTotal) || 0,
      discountType: Number(formData.discountType) || 0,
      discount: Number(formData.discount) || 0,
      taxPercentage: Number(formData.taxPercentage) || 0,
      taxAmount: Number(formData.taxAmount) || 0,
      total: Number(formData.total || formData.subTotal) || 0,

      // Status and notes - ensure numbers are valid
      status: Number(formData.statusId || formData.status) || 7,
      note: formData.note || "",
      instruction: formData.instruction || "",

      // Preserve document type on update (previously dropped, causing resend to default to Quotation)
      BookingType:
        formData.BookingType ||
        formData.documentType ||
        this.orderData?.bookingType ||
        "Proforma",

      // Payment info - ensure numbers are valid
      paymentDate: new Date().toISOString(),
      receivedAmount: Number(formData.receivedAmount) || 0,
      payAmount:
        Number(
          formData.paidAmount || formData.payAmount || formData.receivedAmount
        ) || 0,
      paymentType: Number(formData.paymentType) || 0,
      paymentNote: formData.paymentNote || "",

      // GUEST COUNT - Ensure valid numbers with proper fallbacks
      numberOfGuests: (() => {
        const guestCount =
          formData.numberOfGuests ||
          formData.guestCount ||
          formData.NumberOfGuests ||
          formData.number_of_guests;
        console.log("🔍 Guest count detection:", {
          numberOfGuests: formData.numberOfGuests,
          guestCount: formData.guestCount,
          NumberOfGuests: formData.NumberOfGuests,
          number_of_guests: formData.number_of_guests,
          finalValue: guestCount,
          willUse:
            guestCount !== undefined && guestCount !== null
              ? Number(guestCount)
              : 1,
        });
        return guestCount !== undefined && guestCount !== null
          ? Number(guestCount)
          : 1;
      })(),
      NumberOfGuests: (() => {
        const guestCount = formData.NumberOfGuests || formData.numberOfGuests;
        return guestCount !== undefined && guestCount !== null
          ? Number(guestCount)
          : 1;
      })(),

      // ORDER DETAILS - Use the serviceDetails that AddEditBooking.jsx already built properly
      orderDetails: this.formatOrderDetails(formData.serviceDetails || []),

      // SUB CUSTOMERS - Use the subCustomers that AddEditBooking.jsx already built
      subCustomers: this.formatSubCustomers(formData.subCustomers || []),
    };

    console.log("=== SIMPLE PAYLOAD BUILT ===");
    console.log("numberOfGuests in payload:", payload.numberOfGuests);
    console.log("NumberOfGuests in payload:", payload.NumberOfGuests);
    console.log("Original formData guest fields:", {
      numberOfGuests: formData.numberOfGuests,
      NumberOfGuests: formData.NumberOfGuests,
      number_of_guests: formData.number_of_guests,
    });
    console.log("Order Details count:", payload.orderDetails.length);
    console.log("Sub Customers count:", payload.subCustomers.length);
    console.log("Full payload:", payload);

    return payload;
  }

  /**
   * Format order details - SIMPLE conversion
   */
  formatOrderDetails(serviceDetails) {
    console.log("=== FORMAT ORDER DETAILS ===");
    console.log("Raw serviceDetails:", serviceDetails);

    if (!Array.isArray(serviceDetails) || serviceDetails.length === 0) {
      console.log("❌ No service details provided or invalid format");
      return [];
    }

    const formatted = serviceDetails
      .map((service, index) => {
        console.log(`Formatting service ${index}:`, service);

        // Simple validation
        const serviceId = Number(service.serviceID || service.serviceId || 0);
        const price = Number(
          service.serviceRate || service.detailPrice || service.rate || 0
        );

        if (serviceId <= 0) {
          console.log(`❌ Skipping service with invalid ID: ${serviceId}`);
          return null;
        }

        if (price <= 0) {
          console.log(`❌ Skipping service with invalid price: ${price}`);
          return null;
        }

        const formatted = {
          // Backend C# model expects PascalCase property names (OrderDetailDto class)
          OrderDetailPrimaryID: Number(
            service.orderDetailPrimaryID || service.OrderDetailPrimaryID || 0
          ),
          ServiceID: serviceId,
          ServiceTypeID: Number(
            service.typeID || service.TypeID || service.serviceTypeId || 0
          ),
          TypeID: Number(
            service.typeID || service.TypeID || service.serviceTypeId || 0
          ),
          ServiceQuantity: Number(
            service.serviceQuantity || service.quantity || 1
          ),
          ServicePrice: price,
          DetailPrice: Number(service.detailPrice || price),
          ServiceDetailTotal: Number(service.detailPrice || price),
          ParentID: Number(service.parentID || service.ParentID || 0),
          NightCount: Number(service.nights || 0),
          DefectedQuantity: 0,
          BrandID: 0,
          Length: 0,
          Breadth: 0,
          ColorCode: 0,
          // Forward the guest-linking fields that AddEditBooking.jsx already
          // attaches to each company-guest service. SubCustomerID is the
          // field the backend actually uses to tag each orderDetails line to
          // the correct guest - without it, the backend has no way to know
          // which guest a product/service belongs to and collapses them.
          SubCustomerID: Number(
            service.subCustomerId ||
              service.SubCustomerID ||
              service.customerID ||
              service.CustomerID ||
              0
          ),
          GuestName: service.guestName || service.GuestName || "",
          RoomID: Number(
            service.roomID ?? service.roomId ?? service.RoomID ?? 0
          ),
        };

        console.log(`✅ Formatted service ${index}:`, formatted);
        return formatted;
      })
      .filter((service) => service !== null);

    console.log(`Final formatted services count: ${formatted.length}`);
    return formatted;
  }

  /**
   * Format sub customers - SIMPLE conversion
   */
  formatSubCustomers(subCustomers) {
    console.log("=== FORMAT SUB CUSTOMERS ===");
    console.log("Raw subCustomers:", subCustomers);

    if (!Array.isArray(subCustomers)) {
      console.log("❌ No subCustomers provided or invalid format");
      return [];
    }

    const formatted = subCustomers.map((sub, index) => {
      console.log(`Formatting subcustomer ${index}:`, sub);

      const formatted = {
        CustomerID: Number(sub.customerID || 0),
        CompanyID: Number(sub.companyID || 0),
        CustomerName: sub.customerName || "",
        CustomerEmail: sub.customerEmail || "",
        CheckInDate: sub.checkInDate || sub.customerCheckIn,
        CheckOutDate: sub.checkOutDate || sub.customerCheckOut,
        RoomID: Number(sub.roomID || sub.roomId || 0),
      };

      console.log(`✅ Formatted subcustomer ${index}:`, formatted);
      return formatted;
    });

    console.log(`Final formatted subcustomers count: ${formatted.length}`);
    return formatted;
  }

  /**
   * Debug logging
   */
  logDebugInfo() {
    console.log("=== SIMPLE HANDLER DEBUG ===");
    console.log("Order ID:", this.orderId);
    console.log("Order Data:", this.orderData);
    console.log("Payment Data:", this.paymentData);
  }
}
